/**
 * ProgramAccessContext (P4) — which platform programs the current user holds.
 * (docs/reference/app-plus/IMPLEMENTATION_PLAN.md, P4)
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api-client';   // global 401 → logout handling rides along
import { PLATFORM_MODULES, type PlatformModule } from './modules';

interface ProgramAccessValue {
  /** Program codes the current user is granted. Seeded from cache until loaded. */
  grantedPrograms: string[];
  /** Registry entries the user can see (granted ∩ registered). */
  grantedModules: PlatformModule[];
  loading: boolean;
  hasProgram: (code: string) => boolean;
  refresh: () => Promise<void>;
}

const ProgramAccessContext = createContext<ProgramAccessValue | null>(null);

// Security review S4: the cache is PER-USER (shared-browser hygiene); the
// logout path clears it (AuthContext removes program_grants_cache_* keys).
// UI-only data — every API call is independently authorized.
const grantsCacheKey = (userId: number) => `program_grants_cache_${userId}`;

export function ProgramAccessProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // EM review M3: seed from the last-known grants so the sidebar renders its
  // program groups on the FIRST paint (no empty-sidebar flash — the P4
  // "pixel-identical for MIPS-only users" exit criterion depends on this).
  // MIPS is the safe default: every backfilled user holds it.
  const [grantedPrograms, setGrantedPrograms] = useState<string[]>(() => {
    try {
      if (!user) return ['mips'];
      return JSON.parse(localStorage.getItem(grantsCacheKey(user.id)) || '["mips"]');
    } catch { return ['mips']; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!user) { setGrantedPrograms([]); setLoading(false); return; }
    // Cover the in-flight fetch: after a fresh LOGIN this provider re-runs for
    // the new user while `loading` is still false from the logged-out run —
    // Home then decided its view on empty/stale grants and flashed the wrong
    // screen (prod first-login bug, 2026-07-21). The cached seed still paints
    // the sidebar; only loading-gated consumers (Home) wait.
    setLoading(true);
    try {
      // apiClient, NOT raw fetch: the global 401 handler (session invalidation
      // → logout) must cover this call like every other authenticated request.
      const data = await apiClient.get('/api/programs/mine');
      const programs: string[] = data?.programs ?? [];
      setGrantedPrograms(programs);
      localStorage.setItem(grantsCacheKey(user.id), JSON.stringify(programs));
    } catch {
      // Fail-safe (e.g. mid-deploy 404 before the endpoint exists): MIPS only.
      setGrantedPrograms(['mips']);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const grantedModules = PLATFORM_MODULES.filter(m => grantedPrograms.includes(m.code));

  return (
    <ProgramAccessContext.Provider value={{
      grantedPrograms, grantedModules, loading,
      hasProgram: (code) => grantedPrograms.includes(code),
      refresh,
    }}>
      {children}
    </ProgramAccessContext.Provider>
  );
}

export function useProgramAccess(): ProgramAccessValue {
  const ctx = useContext(ProgramAccessContext);
  if (!ctx) throw new Error('useProgramAccess must be used within ProgramAccessProvider');
  return ctx;
}
